import {
  Directive,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  AuthorizationService,
  UserPermissions,
} from '../../../services/read-write-role/authorization.service';

/**
 * Structural directive to show/hide elements based on permissions
 *
 * @example
 * <button *appHasPermission="'edit'">Edit</button>
 * <button *appHasPermission="'delete'">Delete</button>
 * <button *appHasPermission="'create'">Add New</button>
 * <div *appHasPermission="'view'">View content</div>
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  @Input() appHasPermission: 'view' | 'edit' | 'create' | 'delete' = 'view';

  private readonly authService = inject(AuthorizationService);
  private readonly templateRef = inject(TemplateRef<any>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly destroy$ = new Subject<void>();

  //  Track if view is currently rendered
  private hasView = false;

  ngOnInit(): void {
    this.checkPermission();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkPermission(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        const hasPermission = this.evaluatePermission(permissions);

        // Only update view if state changes
        if (hasPermission && !this.hasView) {
          this.viewContainer.createEmbeddedView(this.templateRef);
          this.hasView = true;
        } else if (!hasPermission && this.hasView) {
          this.viewContainer.clear();
          this.hasView = false;
        }
        //  If hasPermission === hasView, do nothing (no change)
      });
  }

  /**
   * Evaluate if user has the required permission
   * @param permissions - User permissions object
   * @returns true if user has permission, false otherwise
   */
  private evaluatePermission(permissions: UserPermissions): boolean {
    switch (this.appHasPermission) {
      case 'view':
        return permissions.canView;
      case 'edit':
        return permissions.canEdit;
      case 'create':
        return permissions.canCreate;
      case 'delete':
        return permissions.canDelete;
      default:
        return false;
    }
  }
}
